/**
 * Bank Page Component
 * 
 * Main page for managing bank accounts. Displays a table of all accounts
 * with their current status and provides controls for:
 * - Project-based filtering and URL synchronization (?project_id=...)
 * - Real-time multi-field search (account name, login, bank, project, callback URL)
 * - Creating new accounts
 * - Starting/stopping automation
 * - Toggling SMS automation
 * - Deleting accounts
 * 
 * The page auto-refreshes every 10 seconds when auto-update is enabled
 * to show real-time status changes from APLA workers.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  message,
  Modal,
  Select,
  Switch,
  Table,
  Tag,
} from 'antd';
import {
  CloseCircleOutlined,
  ProjectOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  createBank,
  deleteBank,
  getBankCountrys,
  getBankList,
  getBankNames,
  getFullKeyList,
  getProjectList,
  getUserRole,
  switchBankOff,
  switchBankOn,
  switchSmsOff,
  switchSmsOn,
} from '../../services/api';
import styles from './BankPage.module.css';

const { Option } = Select;

const BankPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const role = getUserRole();
  const isAxisOperator = role === 'AXIS_OPERATOR';

  // Read project_id from URL query params (if navigated from Projects page or bookmark)
  const urlProjectId = searchParams.get('project_id');

  // State management
  const [data, setData] = useState([]); // All accounts
  const [loading, setLoading] = useState(false); // Loading state
  const [error, setError] = useState(null); // Error message
  const [isModalVisible, setIsModalVisible] = useState(false); // Create modal visibility
  const [projects, setProjects] = useState([]); // Available projects
  const [bankNames, setBankNames] = useState([]); // Available bank names
  const [bankCountrys, setBankCountrys] = useState([]); // Available countries
  const [keys, setKeys] = useState([]); // Available parser keys
  const [answers, setAnswers] = useState([{ key: '', value: '' }]); // Security Q&A
  const [form] = Form.useForm(); // Ant Design form instance
  const [searchTerm, setSearchTerm] = useState(''); // Search filter
  const [selectedProject, setSelectedProject] = useState(urlProjectId || 'all'); // Project filter ('all' or project_id)
  const [autoUpdate, setAutoUpdate] = useState(false); // Auto-refresh toggle

  // Sync selectedProject with URL searchParams if URL changes
  useEffect(() => {
    const pId = searchParams.get('project_id');
    if (pId) {
      setSelectedProject(pId);
    } else {
      setSelectedProject('all');
    }
  }, [searchParams]);

  /**
   * Fetches payin bank accounts from the API.
   * Accounts page is dedicated to payin statement automation.
   * Sorts by started status (active accounts first).
   */
  const fetchBanks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getBankList({ payout_system: 0 });
      // Only payin accounts (exclude payout accounts)
      const payinAccounts = (response.data || []).filter((bank) => !bank.payout_system);
      const sortedData = payinAccounts.sort((a, b) => b.started - a.started);
      setData(sortedData);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (
        status === 401 ||
        status === 403 ||
        (typeof detail === 'string' &&
          (detail.toLowerCase().includes('token expired') ||
            detail.toLowerCase().includes('invalid token')))
      ) {
        sessionStorage.removeItem('jwtToken');
        navigate('/login');
        return;
      }
      setError(err.response?.data?.detail || 'Error loading data');
      message.error('Error: ' + (err.response?.data?.detail || 'Server unavailable'));
    }
    setLoading(false);
  }, [navigate]);

  /**
   * Fetches available projects for the dropdown.
   */
  const fetchProjects = useCallback(async () => {
    try {
      const response = await getProjectList();
      setProjects(response.data);
    } catch (err) {
      message.error('Failed to load project list.');
    }
  }, []);

  /**
   * Fetches available bank names for the dropdown.
   */
  const fetchBankNames = useCallback(async () => {
    try {
      const response = await getBankNames();
      setBankNames(response.data);
    } catch (err) {
      message.error('Failed to load bank names list.');
    }
  }, []);

  /**
   * Fetches available countries for the dropdown.
   */
  const fetchBankCountrys = useCallback(async () => {
    try {
      const response = await getBankCountrys();
      setBankCountrys(response.data);
    } catch (err) {
      message.error('Failed to load bank countries list.');
    }
  }, []);

  /**
   * Fetches available parser keys for the dropdown.
   */
  const fetchKeys = useCallback(async () => {
    try {
      const response = await getFullKeyList();
      const formattedKeys = response.data.map((key) => ({
        id: key.id,
        display: `${key.bank_name.toUpperCase()} | ${key.type.toUpperCase()}`,
      }));
      setKeys(formattedKeys);
    } catch (err) {
      message.error('Failed to load key list.');
    }
  }, []);

  /**
   * Handles project filter change and updates URL search parameter.
   */
  const handleProjectFilterChange = (value) => {
    const val = value || 'all';
    setSelectedProject(val);
    const newParams = new URLSearchParams(searchParams);
    if (val === 'all') {
      newParams.delete('project_id');
    } else {
      newParams.set('project_id', val);
    }
    setSearchParams(newParams);
  };

  /**
   * Calculates account counts for each project to show in dropdown badges.
   */
  const projectAccountCounts = useMemo(() => {
    const counts = {};
    let unassigned = 0;
    data.forEach((bank) => {
      if (bank.project_id) {
        counts[bank.project_id] = (counts[bank.project_id] || 0) + 1;
      } else if (bank.project_name) {
        const match = projects.find((p) => p.name === bank.project_name);
        if (match) {
          counts[match.id] = (counts[match.id] || 0) + 1;
        } else {
          unassigned += 1;
        }
      } else {
        unassigned += 1;
      }
    });
    return { counts, unassigned, total: data.length };
  }, [data, projects]);

  /**
   * Filters accounts based on selected project and multi-field search term.
   */
  const filteredData = useMemo(() => {
    let result = data;

    // 1. Filter by project
    if (selectedProject && selectedProject !== 'all') {
      if (selectedProject === 'unassigned') {
        result = result.filter((item) => !item.project_id && !item.project_name);
      } else {
        result = result.filter((item) => {
          const itemProjId = item.project_id
            ? String(item.project_id)
            : projects.find((p) => p.name === item.project_name)?.id?.toString();
          return itemProjId === String(selectedProject) || item.project_name === selectedProject;
        });
      }
    }

    // 2. Filter by search term across multiple fields
    if (searchTerm && searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter((item) => {
        const accName = item.account_name?.toLowerCase() || '';
        const bankLogin = item.bank_login?.toLowerCase() || '';
        const bankName = item.name?.toLowerCase() || '';
        const accNum = item.account_number?.toLowerCase() || '';
        const projName = item.project_name?.toLowerCase() || '';
        const callbackUrl = item.callback_url?.toLowerCase() || '';

        return (
          accName.includes(term) ||
          bankLogin.includes(term) ||
          bankName.includes(term) ||
          accNum.includes(term) ||
          projName.includes(term) ||
          callbackUrl.includes(term)
        );
      });
    }

    return result.sort((a, b) => b.started - a.started);
  }, [data, selectedProject, searchTerm, projects]);



  /**
   * Handles account creation form submission.
   */
  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        project_id: values.project_id || (projects[0]?.id || null),
        current_key_id: values.current_key_id || (keys[0]?.id || null),
        answers: isAxisOperator ? [] : answers.filter((a) => a.key && a.value),
        payout_system: values.payout_system !== undefined ? values.payout_system : false,
      };
      await createBank(payload);
      message.success('Account successfully created.');
      fetchBanks();
      setIsModalVisible(false);
      form.resetFields();
      setAnswers([{ key: '', value: '' }]);
    } catch (err) {
      if (err.response?.data) {
        console.error('Error creating account:', err.response.data);
        message.error(`Error creating account: ${JSON.stringify(err.response.data)}`);
      } else {
        message.error('Error creating account.');
        console.error(err);
      }
    }
  };

  // Security answer management functions
  const addAnswer = () => {
    setAnswers([...answers, { key: '', value: '' }]);
  };

  const removeAnswer = (index) => {
    setAnswers(answers.filter((_, i) => i !== index));
  };

  const updateAnswer = (index, field, value) => {
    const updatedAnswers = [...answers];
    updatedAnswers[index][field] = value;
    setAnswers(updatedAnswers);
  };

  // Initial data fetch
  useEffect(() => {
    fetchBanks();
    fetchProjects();
    fetchBankNames();
    fetchBankCountrys();
    fetchKeys();
  }, [fetchBanks, fetchProjects, fetchBankNames, fetchBankCountrys, fetchKeys]);

  // Auto-update interval
  useEffect(() => {
    let interval;
    if (autoUpdate) {
      interval = setInterval(() => {
        fetchBanks();
      }, 10000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [autoUpdate, fetchBanks]);

  /**
   * Returns a colored tag based on account status.
   */
  const getStatusTag = (status) => {
    switch (status) {
      case 'not_working':
        return <Tag color="default">Not working</Tag>;
      case 'working':
        return <Tag color="green">Working</Tag>;
      case 'pending':
        return <Tag color="orange">Pending</Tag>;
      case 'stopping':
        return <Tag color="orange">Stopping</Tag>;
      case 'starting':
        return <Tag color="orange">Starting</Tag>;
      case 'not_downloading':
        return <Tag color="red">Not uploading</Tag>;
      default:
        return <Tag color="default">Unknown</Tag>;
    }
  };

  // Table column definitions
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Account (Login)',
      dataIndex: 'account_name',
      key: 'account_name',
      render: (text, record) =>
        isAxisOperator ? (
          <span style={{ fontWeight: 500 }}>
            {record.account_name} ({record.bank_login})
          </span>
        ) : (
          <Button type="link" onClick={() => navigate(`/account/${record.id}`)} style={{ padding: 0 }}>
            {record.account_name} ({record.bank_login})
          </Button>
        ),
    },
    ...(!isAxisOperator
      ? [

          {
            title: 'Project',
            dataIndex: 'project_name',
            key: 'project_name',
            render: (projectName) =>
              projectName ? (
                <Tag color="geekblue">{projectName}</Tag>
              ) : (
                <Tag color="default">Unassigned</Tag>
              ),
          },
          {
            title: 'Key',
            dataIndex: 'key_type',
            key: 'key_type',
          },
        ]
      : []),
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
    },
    {
      title: isAxisOperator ? 'Last Active' : 'Upload time',
      dataIndex: ['task_info', 'last_upload_at'],
      key: 'last_active_time',
      render: (lastUpload, record) => {
        const ts =
          record.task_info?.last_heartbeat ||
          lastUpload ||
          record.task_info?.last_upload_at ||
          record.task_info?.first_login_at ||
          record.task_info?.first_started_at;

        if (!ts) return '—';

        const date = new Date(ts * 1000);
        if (isNaN(date)) return '—';

        return new Intl.DateTimeFormat('en-IN', {
          timeZone: 'Asia/Kolkata',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
        }).format(date);
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record) => {
        // An account is actively running only if started is true and status is not dead/stopped
        const isDeadOrStopped = ['not_working', 'stopped', 'failed'].includes(record.status);
        const isRunning = Boolean(record.started) && !isDeadOrStopped;
        const buttonText = isRunning ? 'Turn off' : record.started ? 'Restart' : 'Turn on';
        const buttonColor = isRunning ? '#FF4D4F' : record.started ? '#1890FF' : '#52C41A';

        return (
          <div className={styles.buttonGroup}>
            {/* Start/Stop/Restart automation button */}
            <Button
              type="primary"
              style={{
                marginRight: '8px',
                backgroundColor: buttonColor,
                borderColor: buttonColor,
              }}
              onClick={async () => {
                try {
                  if (isRunning) {
                    await switchBankOff(record.id);
                    message.success(`Account ID ${record.id} turned off.`);
                  } else {
                    await switchBankOn(record.id);
                    message.success(`Account ID ${record.id} ${record.started ? 'restarted' : 'turned on'}.`);
                  }
                  fetchBanks();
                } catch (err) {
                  if (err.response?.data?.detail === 'Proxy not found for bank') {
                    message.warning(
                      `No available proxy found for account ID ${record.id}. Add or free up a proxy and try again.`
                    );
                  } else {
                    message.error('Error changing account status.');
                    console.error(err);
                  }
                }
              }}
            >
              {buttonText}
            </Button>

          {/* SMS automation toggle button - Admin only */}
          {!isAxisOperator && (
            <Button
              type="dashed"
              style={{
                marginRight: '8px',
                color: record.sms_automation_enabled ? '#FF4D4F' : '#52C41A',
                borderColor: record.sms_automation_enabled ? '#FF4D4F' : '#52C41A',
              }}
              onClick={async () => {
                try {
                  if (record.sms_automation_enabled) {
                    await switchSmsOff(record.id);
                    message.success(`SMS automation disabled for account ID ${record.id}.`);
                  } else {
                    await switchSmsOn(record.id);
                    message.success(`SMS automation enabled for account ID ${record.id}.`);
                  }
                  fetchBanks();
                } catch (err) {
                  message.error('Error toggling SMS automation.');
                  console.error(err);
                }
              }}
            >
              {record.sms_automation_enabled ? 'SMS Off' : 'SMS On'}
            </Button>
          )}

          {/* Refresh button */}
          <Button onClick={() => fetchBanks()} style={{ marginRight: '8px' }}>
            Refresh
          </Button>

          {/* Delete button with confirmation - Admin only */}
          {!isAxisOperator && (
            <Button
              danger
              onClick={() => {
                Modal.confirm({
                  title: 'Are you sure you want to delete this account?',
                  content: `Account ID ${record.id} will be permanently deleted. This action cannot be undone.`,
                  okText: 'Yes, Delete',
                  okType: 'danger',
                  cancelText: 'Cancel',
                  onOk: async () => {
                    try {
                      await deleteBank(record.id);
                      message.success(`Account ID ${record.id} deleted.`);
                      fetchBanks();
                    } catch (err) {
                      message.error('Error deleting account.');
                      console.error(err);
                    }
                  },
                });
              }}
            >
              Delete
            </Button>
          )}
        </div>
      );
    },
    },
  ];

  return (
    <div className={styles.container}>
      {/* Header with Title and Main Controls */}
      <div className={styles.header}>
        <h2>{isAxisOperator ? 'Axis Bank Account Management' : 'Bank Account Management'}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button type="primary" className={styles.addButton} onClick={() => setIsModalVisible(true)}>
            Add Account
          </Button>
          <div className={styles.autoUpdateSwitch}>
            <span>Auto-update: </span>
            <Switch checked={autoUpdate} onChange={(checked) => setAutoUpdate(checked)} />
          </div>
        </div>
      </div>

      {/* Filter and Search Controls Bar */}
      <div className={styles.toolbar}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', flex: 1 }}>
          {/* Project Dropdown Filter */}
          {!isAxisOperator && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontWeight: 500, color: '#555', fontSize: 13 }}>
                <ProjectOutlined style={{ marginRight: 4, color: '#1890ff' }} />
                Project:
              </span>
              <Select
                value={selectedProject}
                onChange={handleProjectFilterChange}
                style={{ minWidth: 200 }}
                placeholder="Filter by Project"
              >
                <Option value="all">
                  <span>All Projects</span>
                  <Tag color="blue" style={{ marginLeft: 6, fontSize: 11 }}>
                    {projectAccountCounts.total}
                  </Tag>
                </Option>
                {projects.map((proj) => (
                  <Option key={proj.id} value={String(proj.id)}>
                    <span>{proj.name}</span>
                    <Tag color="default" style={{ marginLeft: 6, fontSize: 11 }}>
                      {projectAccountCounts.counts[proj.id] || 0}
                    </Tag>
                  </Option>
                ))}
                {projectAccountCounts.unassigned > 0 && (
                  <Option value="unassigned">
                    <span>Unassigned</span>
                    <Tag color="orange" style={{ marginLeft: 6, fontSize: 11 }}>
                      {projectAccountCounts.unassigned}
                    </Tag>
                  </Option>
                )}
              </Select>
            </div>
          )}

          {/* Multi-Field Search Input */}
          <Input
            placeholder="Search accounts, login, bank, project, callback URL..."
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            allowClear
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchBar}
            style={{ flex: 1, minWidth: 260, maxWidth: 450 }}
          />

          {/* Active Filter Indicators & Reset Button */}
          {(selectedProject !== 'all' || searchTerm.trim() !== '') && (
            <Button
              icon={<CloseCircleOutlined />}
              onClick={() => {
                handleProjectFilterChange('all');
                setSearchTerm('');
              }}
              size="middle"
            >
              Reset Filters
            </Button>
          )}
        </div>

        <div style={{ color: '#888', fontSize: 13, whiteSpace: 'nowrap' }}>
          Showing <strong>{filteredData.length}</strong> of <strong>{data.length}</strong> accounts
        </div>
      </div>



      {/* Error display */}
      {error && (
        <Alert
          className={styles.errorAlert}
          message="Error loading data"
          description={error}
          type="error"
          showIcon
          closable
        />
      )}

      {/* Accounts table */}
      <Table
        dataSource={filteredData}
        columns={columns}
        loading={loading}
        rowKey="id"
        className={styles.table}
        pagination={false}
      />

      {/* Create account modal */}
      <Modal
        title="Add Account"
        visible={isModalVisible}
        onOk={handleCreate}
        onCancel={() => setIsModalVisible(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Bank Name"
            rules={[{ required: true, message: 'Select a bank name' }]}
          >
            <Select placeholder="Select a bank">
              {bankNames.map((name) => (
                <Option key={name} value={name}>
                  {name.toUpperCase()}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="country"
            label="Bank Country"
            rules={[{ required: true, message: 'Select a country' }]}
          >
            <Select placeholder="Select a country">
              {bankCountrys.map((name) => (
                <Option key={name} value={name}>
                  {name.toUpperCase()}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="account_name" label="Account Name">
            <Input placeholder="Enter account name" />
          </Form.Item>
          <Form.Item name="bank_login" label="Login" rules={[{ required: true, message: 'Login is required' }]}>
            <Input placeholder="Enter login" />
          </Form.Item>
          <Form.Item name="bank_user_id" label="User ID">
            <Input placeholder="Enter user ID" />
          </Form.Item>
          <Form.Item name="bank_password" label="Password" rules={[{ required: true, message: 'Password is required' }]}>
            <Input.Password placeholder="Enter password" />
          </Form.Item>
          <Form.Item name="phone_number" label="Phone Number">
            <Input placeholder="Enter phone number" />
          </Form.Item>
          <Form.Item name="account_number" label="Account Number">
            <Input placeholder="Enter account number" />
          </Form.Item>
          <Form.Item name="corporate" valuePropName="checked">
            <Checkbox>Corporate Account</Checkbox>
          </Form.Item>
          {!isAxisOperator && (
            <>
              <Form.Item
                name="project_id"
                label="Project"
                rules={[{ required: true, message: 'Select a project' }]}
              >
                <Select placeholder="Select a project">
                  {projects.map((project) => (
                    <Option key={project.id} value={project.id}>
                      {project.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="current_key_id"
                label="Key"
                rules={[{ required: true, message: 'Select a key' }]}
              >
                <Select placeholder="Select a key">
                  {keys.map((key) => (
                    <Option key={key.id} value={key.id}>
                      {key.display}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="Security Answers">
                {answers.map((answer, index) => (
                  <div key={index} style={{ display: 'flex', marginBottom: '8px' }}>
                    <Input
                      placeholder="Key"
                      value={answer.key}
                      onChange={(e) => updateAnswer(index, 'key', e.target.value)}
                      style={{ marginRight: '8px' }}
                    />
                    <Input
                      placeholder="Value"
                      value={answer.value}
                      onChange={(e) => updateAnswer(index, 'value', e.target.value)}
                      style={{ marginRight: '8px' }}
                    />
                    <Button danger onClick={() => removeAnswer(index)}>
                      Delete
                    </Button>
                  </div>
                ))}
                <Button type="dashed" onClick={addAnswer} style={{ marginTop: '8px' }}>
                  Add Pair
                </Button>
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default BankPage;
